import React from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AppSelect } from '../../constants/appSelect';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurriculumInfo, updateCurriculum } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import { uploadImageCDN, checkValidUpload } from '../../utils/utility';
import { useEffect } from 'react';
import BundledEditor from '../../components/BundledEditor';

const itemName = 'Chương trình học';
const { Text, Title } = Typography;

const EditItem = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const { data: itemInfo } = useQuery({
      queryKey: ['itemInfo', id],
      queryFn: () => getCurriculumInfo(id),
    });

  // categories for select (only type = 2: Danh mục khóa học) and display as hierarchical 'Parent — Child — Leaf'
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategories'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const categoryOptions = React.useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const parentMap = all.reduce((acc, cur) => { acc[cur.id] = cur.parent_id || 0; return acc; }, {});
    const titleMap = all.reduce((acc, cur) => { acc[cur.id] = cur.title || ''; return acc; }, {});
    const items = all.filter((c) => Number(c?.type) === 2);
    const toPath = (id) => {
      const parts = [];
      let cur = id;
      const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) {
        guard.add(cur);
        parts.unshift(titleMap[cur] || '');
        cur = parentMap[cur] || 0;
      }
      return parts.filter(Boolean);
    };
    const opts = items.map((c) => {
      const path = toPath(c.id);
      return { value: c.id, label: path.join(' — ') };
    });
    // sort by label so children follow their parents
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [allCategoriesRes]);

  useEffect(() => {
      if (itemInfo) {
        form.setFieldsValue({
          ...itemInfo,
          title: itemInfo?.title,
          short_description: typeof itemInfo?.short_description === 'string' ? itemInfo.short_description : '',
          description: typeof itemInfo?.description === 'string' ? itemInfo.description : '',
          thumb: itemInfo?.thumb,
          category_id: itemInfo?.category_id ?? 0,
          status: itemInfo?.status,
        });
      }
    }, [form, itemInfo]);

  // handle submit form
  const { mutate: mutateUpdateItem, isPending } = useMutation({
    mutationFn: ({ id, payload }) => updateCurriculum(id, payload),
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: `Cập nhật ${itemName} thành công!`,
        });
        navigate(-1);
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...',
      });
    },
  });

  const handleSubmitForm = (data) => {
    const dataSubmit = {
      title: data?.title,
      short_description: data?.short_description || '',
      description: data?.description,
      thumb: data?.thumb,
      category_id: Number(data?.category_id || 0),
      status: data?.status,
    };
    mutateUpdateItem({ id, payload: dataSubmit });
  };

  const handleUploadThumb = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) {
      return false;
    }
    const url = await uploadImageCDN(file, () => {}, 'toancohien_curriculum');
    if (url) {
      notification.success({
        message: 'Tải lên thành công!',
      });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ thumb: safeUrl });
    }
    return false;
  };

  return (
    <Form
      name="edit-item"
      form={form}
      layout="vertical"
      requiredMark={false}
      className="mb-5"
      onFinish={handleSubmitForm}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Sửa {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="title"
            label={
              <Text>
                Tên {itemName} <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: `Vui lòng nhập tên ${itemName}` }]}
          >
            <Input placeholder={`Nhập tên ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="short_description" label={<Text>Mô tả ngắn</Text>}>
            <Input.TextArea placeholder={`Nhập mô tả ngắn ${itemName}...`} autoSize={{ minRows: 2, maxRows: 5 }} maxLength={1000} showCount />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="description"
            label={<Text>Mô tả (HTML)</Text>}
            valuePropName="value"
            trigger="onEditorChange"
            getValueFromEvent={(content) => (typeof content === 'string' ? content : '')}
          >
            <BundledEditor
              init={{
                license_key: 'gpl',
                height: 400,
                menubar: false,
                automatic_uploads: true,
                file_picker_types: 'image',
                plugins: [
                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor', 'searchreplace',
                  'visualblocks', 'code', 'fullscreen', 'insertdatetime', 'media', 'table', 'help', 'wordcount'
                ],
                toolbar:
                  'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | ' +
                  'bullist numlist outdent indent | image | removeformat | code | help',
                file_picker_callback: (cb, value, meta) => {
                  const input = document.createElement('input');
                  input.setAttribute('type', 'file');
                  input.setAttribute('accept', 'image/*');
                  input.onchange = async () => {
                    const file = input.files[0];
                    if (!checkValidUpload(file)) return;
                    const url = await uploadImageCDN(file, () => {}, 'toancohien_curriculum');
                    if (url) {
                      const safeUrl = encodeURI(url);
                      cb(safeUrl, { title: file.name });
                    }
                  };
                  input.click();
                },
                images_upload_handler: async (blobInfo, progress) => {
                  const blob = blobInfo.blob();
                  if (!checkValidUpload(blob)) return '';
                  const url = await uploadImageCDN(blob, () => {}, 'toancohien_curriculum');
                  return encodeURI(url || '');
                },
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="category_id"
            label={<Text>Danh mục <span className="text-red-700">*</span></Text>}
            rules={[
              {
                validator: (_, value) => (Number(value || 0) > 0 ? Promise.resolve() : Promise.reject(new Error('Vui lòng chọn danh mục'))),
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Chọn danh mục"
              optionFilterProp="label"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={categoryOptions}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="thumb"
            label={
              <Text>
                Ảnh đại diện
              </Text>
            }
          >
            <Input
              placeholder={`Nhập URL hoặc tải lên file...`}
              addonBefore={
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleUploadThumb}
                  className="hover:cursor-pointer"
                >
                  <UploadOutlined />
                  Upload
                </Upload>
              }
            />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="status"
            label={
              <Text>
                Trạng thái <span className="text-red-700">*</span>
              </Text>
            }
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
          >
            <Select
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              placeholder="Chọn trạng thái"
              options={AppSelect.STATUS_CURRICULUM || AppSelect.STATUS}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default EditItem;
